import { Blocks, Plug, Users } from "lucide-react";
import { SectionHeading, SECTION_CLASS } from "./SectionHeading";

const STEPS = [
  {
    title: "Build",
    description: "Create a knowledge base from your client's documents, an agent with tools, or a LangGraph workflow in the dashboard. It's hosted for you.",
    icon: Blocks,
  },
  {
    title: "Connect",
    description: "Call it as a model name from the official OpenAI SDK, on any of 20+ providers. Switching the model is one string.",
    icon: Plug,
  },
  {
    title: "Resell",
    description: "Give each client their own account with a wallet, API key and usage. Your markup is added to their requests and credited to your wallet.",
    icon: Users,
  },
];

const CODE_SAMPLE = `from openai import OpenAI

client = OpenAI(
    base_url="https://api.genaicore.com/v1",
    api_key="YOUR_API_KEY",
)

resp = client.chat.completions.create(
    model="rag:kb-123@anthropic/claude-sonnet-5-5",
    messages=[{"role": "user", "content": "What is the refund policy?"}],
)
print(resp.choices[0].message.content)`;

export function HowItWorks() {
  return (
    <section id="how-it-works" className={SECTION_CLASS}>
      <SectionHeading
        eyebrow="How it works"
        title="Build once."
        accent="Resell to every client, on any model."
        className="mb-12 sm:mb-16"
      />

      <div className="grid gap-4 md:grid-cols-3">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={step.title} className="rounded-card bg-card p-6 flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <span className="h-10 w-10 rounded-xl bg-surface-muted flex items-center justify-center">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <span className="text-[13px] text-subtle tabular">0{i + 1}</span>
              </div>
              <h3 className="text-lg font-medium">{step.title}</h3>
              <p className="text-[15px] text-muted-foreground mt-2 leading-relaxed">{step.description}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-4 rounded-card bg-card p-6 sm:p-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-center">
        <div>
          <h3 className="text-[22px] font-medium tracking-tight">Your knowledge base is a model name</h3>
          <p className="text-[15px] text-muted-foreground mt-3 leading-relaxed">
            <code className="font-fragment text-[13px]">rag:&lt;kb-id&gt;</code>, <code className="font-fragment text-[13px]">agent:&lt;id&gt;</code> and{" "}
            <code className="font-fragment text-[13px]">langgraph:&lt;id&gt;</code> work wherever a model name does. Add{" "}
            <code className="font-fragment text-[13px]">@provider/model</code> to choose the model that answers.
          </p>
        </div>
        <pre className="rounded-xl bg-surface-muted p-4 sm:p-5 font-fragment text-[13px] leading-relaxed overflow-x-auto text-foreground min-w-0">
          {CODE_SAMPLE}
        </pre>
      </div>
    </section>
  );
}
